import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import {
  EnseignantExterneHeader,
  EnseignantExterneList,
  EnseignantExterneForm,
  EnseignantExterneDetails
} from './enseignants';

const EnseignantsExternesTab = ({ onDataChange }) => {
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEnseignant, setSelectedEnseignant] = useState(null);
  const [detailEnseignant, setDetailEnseignant] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    titre: 'DR',
    specialite: '',
    etablissement_origine: '',
    grade: '',
    telephone: '',
    adresse: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [enseignantToDelete, setEnseignantToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const fetchEnseignants = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await api.get('/soutenance/enseignants-externes/', { params });
      
      if (response.data.status === 'success') {
        setEnseignants(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors du chargement');
      toast.error('Erreur lors du chargement des enseignants externes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchEnseignants();
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    fetchEnseignants();
  }, [fetchEnseignants]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOpenModal = (enseignant = null) => {
    if (enseignant) {
      setSelectedEnseignant(enseignant);
      setFormData({
        username: enseignant.utilisateur?.username || '',
        email: enseignant.utilisateur?.email || '',
        first_name: enseignant.utilisateur?.first_name || '',
        last_name: enseignant.utilisateur?.last_name || '',
        password: '',
        confirmPassword: '',
        titre: enseignant.titre,
        specialite: enseignant.specialite,
        etablissement_origine: enseignant.etablissement_origine,
        grade: enseignant.grade || '',
        telephone: enseignant.telephone || '',
        adresse: enseignant.adresse || ''
      });
    } else {
      setSelectedEnseignant(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirmPassword: '',
        titre: 'DR',
        specialite: '',
        etablissement_origine: '',
        grade: '',
        telephone: '',
        adresse: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!selectedEnseignant) {
      if (!formData.username.trim()) errors.username = "Le nom d'utilisateur est requis";
      if (!formData.email.trim()) {
        errors.email = "L'email est requis";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = "Format d'email invalide";
      }
      if (!formData.first_name.trim()) errors.first_name = "Le prénom est requis";
      if (!formData.last_name.trim()) errors.last_name = "Le nom est requis";
      if (!formData.password.trim()) {
        errors.password = "Le mot de passe est requis";
      } else if (formData.password.length < 8) {
        errors.password = "Le mot de passe doit contenir au moins 8 caractères";
      }
      
      if (!formData.confirmPassword.trim()) {
        errors.confirmPassword = "La confirmation du mot de passe est requise";
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }
    
    if (!formData.titre) errors.titre = "Le titre est requis";
    if (!formData.specialite.trim()) errors.specialite = "La spécialité est requise";
    if (!formData.etablissement_origine.trim()) errors.etablissement_origine = "L'établissement d'origine est requis";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      let dataToSend;
      if (selectedEnseignant) {
        dataToSend = {
          titre: formData.titre,
          specialite: formData.specialite,
          etablissement_origine: formData.etablissement_origine,
          grade: formData.grade,
          telephone: formData.telephone,
          adresse: formData.adresse
        };
      } else {
        // Exclure confirmPassword des données à envoyer
        const { confirmPassword, ...dataWithoutConfirm } = formData;
        dataToSend = dataWithoutConfirm;
      }

      if (selectedEnseignant) {
        await api.put(`/soutenance/enseignants-externes/${selectedEnseignant.id}/`, dataToSend);
        toast.success('Enseignant externe modifié avec succès');
      } else {
        await api.post('/soutenance/enseignants-externes/', dataToSend);
        toast.success('Enseignant externe créé avec succès');
      }

      setIsModalOpen(false);
      await fetchEnseignants();
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (enseignant) => {
    setDetailEnseignant(enseignant);
    setIsDetailModalOpen(true);
  };

  const handlePrepareDelete = (enseignant) => {
    setEnseignantToDelete(enseignant);
    setConfirmConfig({
      title: 'Désactiver l\'enseignant externe',
      message: `Êtes-vous sûr de vouloir désactiver l'enseignant externe "${enseignant.nom_complet}" ?`,
      confirmText: 'Désactiver',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!enseignantToDelete) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/soutenance/enseignants-externes/${enseignantToDelete.id}/`);
      toast.success('Enseignant externe désactivé avec succès');
      
      setIsConfirmModalOpen(false);
      setEnseignantToDelete(null);
      await fetchEnseignants();
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la désactivation';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        disabled={submitting}
      >
        Annuler
      </button>
      <button
        type="submit"
        form="enseignant-form"
        disabled={submitting}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        {submitting && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        )}
        <span>{selectedEnseignant ? 'Modifier' : 'Créer'}</span>
      </button>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <EnseignantExterneHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddNew={() => handleOpenModal()}
        />

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Contenu principal */}
        <div className="mt-6">
          <EnseignantExterneList
            enseignants={enseignants}
            loading={loading}
            searchTerm={searchTerm}
            onEdit={handleOpenModal}
            onDelete={handlePrepareDelete}
            onViewDetails={handleViewDetails}
            onAddNew={() => handleOpenModal()}
          />
        </div>

        {/* Pagination */}
        {pagination && enseignants.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* Modal principal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedEnseignant ? 'Modifier l\'Enseignant' : 'Nouvel Enseignant Externe'}
          footer={renderModalFooter()}
          maxWidth="max-w-4xl"
        >
          <EnseignantExterneForm
            formData={formData}
            formErrors={formErrors}
            selectedEnseignant={selectedEnseignant}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        </Modal>

        {/* Modal de détails */}
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title="Détails de l'enseignant externe"
          maxWidth="max-w-2xl"
        >
          <EnseignantExterneDetails enseignant={detailEnseignant} />
        </Modal>

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default EnseignantsExternesTab;